import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import html2canvas from "html2canvas";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const AddSite = () => {
  const { accessToken } = useAuth();
  const baseURL = process.env.REACT_APP_BASEURL;
  const mapRef = useRef(null);

  // State
  const [siteName, setSiteName] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [mainUser, setMainUser] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [capturingImage, setCapturingImage] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [isValidCoordinates, setIsValidCoordinates] = useState(true);
  const [mapEnabled, setMapEnabled] = useState(false);
  const [capturedBase64, setCapturedBase64] = useState("");
  const [ndviImage, setNdviImage] = useState(null);
  const [ndviImagePreview, setNdviImagePreview] = useState(null);
  const [ndviImageError, setNdviImageError] = useState(null);

  // Satellite tile layer
  const tileLayer = {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
  };

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      setError(null);
      try {
        const response = await axios.get(`${baseURL}/users/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userList = response.data || [];
        setUsers(userList);
        if (userList.length > 0) {
          setMainUser(userList[0].id);
        }
      } catch (err) {
        setError("Failed to load users. Please try again.");
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [accessToken, baseURL]);

  // Validate and update map center when coordinates change
  useEffect(() => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
    const isValidLon = !isNaN(lon) && lon >= -180 && lon <= 180;
    setIsValidCoordinates(isValidLat && isValidLon);
    if (isValidLat && isValidLon) {
      setMapCenter([lat, lon]);
    }
  }, [latitude, longitude]);

  // Cleanup NDVI image preview URL
  useEffect(() => {
    return () => {
      if (ndviImagePreview) {
        URL.revokeObjectURL(ndviImagePreview);
      }
    };
  }, [ndviImagePreview]);

  // Handle NDVI image selection
  const handleNdviImageChange = (e) => {
    const file = e.target.files[0];
    setNdviImageError(null);
    if (file) {
      // Validate file type and size
      const allowedTypes = ["image/jpeg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        setNdviImageError("Only JPG and PNG images are allowed.");
        setNdviImage(null);
        setNdviImagePreview(null);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setNdviImageError("Image must be less than 5MB.");
        setNdviImage(null);
        setNdviImagePreview(null);
        return;
      }
      setNdviImage(file);
      setNdviImagePreview(URL.createObjectURL(file));
    } else {
      setNdviImage(null);
      setNdviImagePreview(null);
    }
  };

  // Capture map as base64 image (336x224 pixels, without marker and labels)
  const captureMapImage = async () => {
    if (!mapRef.current || !isValidCoordinates || !mapEnabled) {
      setError("Cannot capture map image. Ensure valid coordinates and map are enabled.");
      return null;
    }

    setCapturingImage(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const mapElement = mapRef.current;
      const canvas = await html2canvas(mapElement, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
        width: 336,
        height: 224,
        ignoreElements: (element) =>
          element.classList.contains("leaflet-control-container") ||
          element.classList.contains("leaflet-control") ||
          element.classList.contains("leaflet-marker-pane") ||
          element.classList.contains("leaflet-marker-icon") ||
          element.classList.contains("leaflet-marker-shadow"),
      });
      const base64Image = canvas.toDataURL("image/png");
      setCapturedBase64(base64Image);
      return base64Image;
    } catch (err) {
      setError("Failed to capture map image. Please try again.");
      return null;
    } finally {
      setCapturingImage(false);
    }
  };

  // Map component to update coordinates on click
  const MapUpdater = ({ center, onClick }) => {
    const map = useMap();
    useEffect(() => {
      if (center[0] !== 0 && center[1] !== 0 && mapEnabled) {
        map.setView(center, 15);
      }
    }, [center, map, mapEnabled]);

    useEffect(() => {
      const handleClick = (e) => {
        const { lat, lng } = e.latlng;
        setLatitude(lat.toFixed(6).toString());
        setLongitude(lng.toFixed(6).toString());
        onClick([lat, lng]);
      };
      map.on("click", handleClick);
      return () => map.off("click", handleClick);
    }, [map, onClick]);

    return null;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoadingSubmit(true);

    // Validate inputs
    if (!siteName.trim()) {
      setError("Site name is required.");
      setLoadingSubmit(false);
      return;
    }
    if (!latitude || !longitude || !isValidCoordinates) {
      setError("Valid latitude and longitude are required.");
      setLoadingSubmit(false);
      return;
    }
    const parsedMainUser = Number(mainUser);
    if (!parsedMainUser || !Number.isInteger(parsedMainUser) || parsedMainUser <= 0) {
      setError("A valid main user must be selected.");
      setLoadingSubmit(false);
      return;
    }
    if (ndviImageError) {
      setError(ndviImageError);
      setLoadingSubmit(false);
      return;
    }

    try {
      // Capture map image if not already captured
      let imageToSend = capturedBase64;
      if (!imageToSend) {
        imageToSend = await captureMapImage();
        if (!imageToSend) {
          setLoadingSubmit(false);
          return;
        }
      }

      // Prepare FormData
      const formData = new FormData();
      formData.append("name", siteName);
      formData.append("latitude", latitude.toString());
      formData.append("longitude", longitude.toString());
      formData.append(
        "offline_notifications",
        JSON.stringify({
          emails: [],
          enabled: emailNotifications,
          triggers: [],
        })
      );
      formData.append("main_user", String(parsedMainUser));
      if (imageToSend) {
        formData.append("image", imageToSend);
      }
      if (ndviImage) {
        formData.append("ndvi_image", ndviImage);
      }

      await axios.post(`${baseURL}/create-sites`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
        params: { with_image: imageToSend ? true : null },
      });

      setSuccess("Site added successfully!");
      // Reset form
      setSiteName("");
      setLatitude("");
      setLongitude("");
      setEmailNotifications(true);
      setMainUser(users.length > 0 ? users[0].id : "");
      setMapCenter([0, 0]);
      setMapEnabled(false);
      setCapturedBase64("");
      setNdviImage(null);
      setNdviImagePreview(null);
    } catch (err) {
      if (err.response) {
        setError(err.response.data.message || "Failed to add site. Please try again.");
      } else if (err.request) {
        setError("No response from server. Please try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Get user display name
  const getUserDisplayName = (user) => {
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return fullName || user.username || `User ${user.id}`;
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}>
        Add New Site
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {capturedBase64 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Map image captured successfully (336x224 base64 string ready for submission).
        </Alert>
      )}
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Site Name */}
          <TextField
            label="Site Name"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            fullWidth
            required
            variant="outlined"
          />
          {/* Location */}
          <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
            Location
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Latitude"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              fullWidth
              type="text"
              required
              error={!isValidCoordinates && latitude !== ""}
              helperText={
                !isValidCoordinates && latitude !== "" ? "Latitude must be between -90 and 90" : ""
              }
            />
            <TextField
              label="Longitude"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              fullWidth
              type="text"
              required
              error={!isValidCoordinates && longitude !== ""}
              helperText={
                !isValidCoordinates && longitude !== "" ? "Longitude must be between -180 and 180" : ""
              }
            />
          </Box>
          {/* Map Section */}
          <Box sx={{ height: 224, width: 336, border: "1px solid #ddd", mb: 2 }}>
            {mapEnabled && isValidCoordinates && latitude && longitude ? (
              <div ref={mapRef} style={{ height: "100%", width: "100%" }}>
                <MapContainer
                  center={mapCenter}
                  zoom={15}
                  style={{ height: "100%", width: "100%" }}
                  zoomControl={!capturingImage}
                  attributionControl={!capturingImage}
                >
                  <TileLayer url={tileLayer.url} attribution={capturingImage ? "" : tileLayer.attribution} />
                  {!capturingImage && (
                    <TileLayer
                      url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                      attribution=""
                    />
                  )}
                  {!capturingImage && <Marker position={mapCenter} />}
                  <MapUpdater center={mapCenter} onClick={setMapCenter} />
                </MapContainer>
              </div>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.100",
                  cursor: "pointer",
                }}
                onClick={() => setMapEnabled(true)}
              >
                <Typography color="text.secondary">
                  {latitude && longitude ? "Click the map to set coordinates" : "Click to enable map"}
                </Typography>
              </Box>
            )}
          </Box>
          {/* NDVI Image Upload */}
          <Box>
            <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, mb: 1 }}>
              NDVI Image (Optional)
            </Typography>
            <Button
              variant="contained"
              component="label"
              sx={{ mb: 2 }}
            >
              Upload NDVI Image
              <input
                type="file"
                hidden
                accept="image/jpeg,image/png"
                onChange={handleNdviImageChange}
              />
            </Button>
            {ndviImageError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {ndviImageError}
              </Alert>
            )}
            {ndviImagePreview && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Preview:
                </Typography>
                <img
                  src={ndviImagePreview}
                  alt="NDVI Preview"
                  style={{ maxWidth: "200px", maxHeight: "200px", borderRadius: "4px" }}
                />
              </Box>
            )}
          </Box>
          {/* Email Notifications */}
          <FormControlLabel
            control={
              <Checkbox
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
              />
            }
            label="Send email notifications"
          />
          {/* Main User */}
          <FormControl fullWidth variant="outlined" disabled={loadingUsers}>
            <InputLabel id="main-user-select-label">Main User</InputLabel>
            <Select
              labelId="main-user-select-label"
              value={mainUser}
              onChange={(e) => setMainUser(e.target.value)}
              label="Main User"
              required
            >
              {loadingUsers ? (
                <MenuItem value="" disabled>
                  Loading users...
                </MenuItem>
              ) : users.length === 0 ? (
                <MenuItem value="" disabled>
                  No users available
                </MenuItem>
              ) : (
                users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {getUserDisplayName(user)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loadingSubmit || loadingUsers || capturingImage || ndviImageError}
            sx={{ mt: 2, alignSelf: "flex-end" }}
          >
            {loadingSubmit ? <CircularProgress size={24} /> : "Add Site"}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddSite;
